/**
 * CoPilotEngineProvider (business-web) — hoists the ONE Protector engine to the
 * app root (0089).
 *
 * Before this, `useCoPilotBrainCore` was called (via `useBusinessCoPilot`)
 * INSIDE `CoPilot`, which lives in the per-page `BusinessDashboardLayout`.
 * Navigating page→page remounted that layout, re-initialised the engine, and
 * lost the thread — the reset bug. Mounting the engine once here, above the
 * router, means navigation can never unmount it: the thread (and
 * `currentConversationId`) survives close → navigate → reopen.
 *
 * All this provider supplies is what makes the instance *business*: portal,
 * the open talent from the sidebar context (page-context → "this talent"), and
 * the Protector action that consumes an attachment.
 */

import {
  useCoPilotBrainCore,
  type IUseCoPilotBrainReturn,
  type CoPilotTurnContext,
} from '@tonnie/copilot-core';
import { browserStorage } from '@tonnie/copilot-ui';
import { createContext, useCallback, useContext, type ReactNode } from 'react';

import { useCoPilotSidebar } from '@/core/contexts/CoPilotSidebarContext';

/**
 * The Protector action that CONSUMES the composer's attachment — a successful
 * confirm clears the pending file. Business-owned document push (see
 * `save_vetting_document` in business-companion-actions.service.ts).
 */
const BUSINESS_FILE_ACTIONS = ['save_vetting_document'] as const;

const CoPilotEngineContext = createContext<IUseCoPilotBrainReturn | null>(null);

export function CoPilotEngineProvider({ children }: { children: ReactNode }) {
  const { isOpen, pageEntity } = useCoPilotSidebar();

  // Resolved per turn so the Protector always sees the CURRENTLY open talent
  // drawer — this is what lets "vet this talent" resolve without a name.
  const resolveContext = useCallback(
    (): CoPilotTurnContext => ({
      portal: 'business',
      entityId: pageEntity?.id,
      entityType: pageEntity?.type,
      journeyInstanceId: pageEntity?.journeyId,
    }),
    [pageEntity?.id, pageEntity?.type, pageEntity?.journeyId]
  );

  const brain = useCoPilotBrainCore(undefined, isOpen, {
    resolveContext,
    storage: browserStorage,
    fileActionIds: BUSINESS_FILE_ACTIONS,
  });

  return <CoPilotEngineContext.Provider value={brain}>{children}</CoPilotEngineContext.Provider>;
}

/** Read the app-root Protector engine. Throws if used outside the provider. */
export function useCoPilotEngine(): IUseCoPilotBrainReturn {
  const ctx = useContext(CoPilotEngineContext);
  if (!ctx) {
    throw new Error('useCoPilotEngine must be used within <CoPilotEngineProvider>');
  }
  return ctx;
}
